// src/pages/Dashboard.tsx
import { useEffect, useMemo, useState, useCallback } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Building2,
  Mail,
  BarChart3,
  RefreshCw,
  Search,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  Eye,
  Download,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/hooks/use-toast";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { ProgressLogger } from "@/components/ProgressLogger";

const API_BASE = import.meta.env.VITE_API_URL ?? "/api";

function cleanBearer(t?: string | null) {
  let raw = (t || "").trim();
  if (raw.toLowerCase().startsWith("bearer ")) raw = raw.slice(7).trim();
  return raw;
}

type ReportOut = {
  id: number;
  name: string;
  description?: string | null;
  sql?: string | null;
  procedure?: string | null;
  active: boolean;
  createdAt?: string | null;
};

type PreviewData = {
  columns: string[];
  rows: Record<string, any>[];
  report?: { id: number; name: string };
  row_count?: number;
  limited?: boolean;
};

export default function Dashboard() {
  const { user } = useAuth();
  const { toast } = useToast();

  const [loading, setLoading] = useState(true);
  const [reports, setReports] = useState<ReportOut[]>([]);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [sendingId, setSendingId] = useState<number | null>(null);

  // état de téléchargement par rapport
  const [downloading, setDownloading] = useState<Record<number, boolean>>({});
  
  // format de fichier pour le téléchargement
  const [fileFormat, setFileFormat] = useState<"csv" | "txt" | "xlsx">("csv");
  
  // état pour le dialog de progression
  const [downloadProgress, setDownloadProgress] = useState<{
    reportId: number;
    sessionId: string;
  } | null>(null);

  // preview dialog
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewBusy, setPreviewBusy] = useState(false);
  const [previewTitle, setPreviewTitle] = useState<string>("");
  const [preview, setPreview] = useState<PreviewData | null>(null);

  // Recherche + pagination
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 10;

  const authHeaders = useMemo(() => {
    const stored = localStorage.getItem("auth_token");
    const token = cleanBearer(stored);
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const url = `${API_BASE}/me/reports`;
      const res = await fetch(url, { headers: { Accept: "application/json", ...authHeaders } });
      if (!res.ok) throw new Error(`Chargement des rapports impossible (${res.status})`);
      const data = await res.json();
      const arr: ReportOut[] = Array.isArray(data) ? data : (data?.reports ?? []);
      setReports(arr || []);
      setPage(1);
    } catch (e: any) {
      toast({ title: "Erreur", description: e?.message || "Chargement échoué", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  }, [authHeaders, toast]);

  useEffect(() => {
    load();
  }, [load]);

  // Filtrage local
  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return reports;
    return reports.filter((r) => {
      const inName = r.name.toLowerCase().includes(term);
      const inId = String(r.id).includes(term);
      const inDesc = (r.description || "").toLowerCase().includes(term);
      return inName || inId || inDesc;
    });
  }, [reports, q]);

  // Pagination
  const total = filtered.length;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageItems = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return filtered.slice(start, start + PAGE_SIZE);
  }, [filtered, currentPage]);

  useEffect(() => setPage(1), [q]);

  // ---------- Actions ----------
  const doSendMail = async (r: ReportOut) => {
    try {
      setSendingId(r.id);
      const res = await fetch(`${API_BASE}/reports/${r.id}/send-email?format=${fileFormat}`, {
        method: "POST",
        headers: { Accept: "application/json", ...authHeaders },
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d?.detail || `Envoi impossible (${res.status})`);
      toast({
        title: "Rapport envoyé ✅",
        description: `Le rapport « ${r.name} » a été envoyé à ${user?.email || "vous"} au format ${fileFormat.toUpperCase()}.`,
      });
    } catch (e: any) {
      toast({ title: "Erreur d'envoi", description: e?.message || "Erreur inconnue", variant: "destructive" });
    } finally {
      setSendingId(null);
    }
  };

  const doPreview = async (r: ReportOut) => {
    setPreviewBusy(true);
    setPreviewOpen(true);
    setPreviewTitle(`Prévisualisation — ${r.name}`);
    try {
      const res = await fetch(`${API_BASE}/reports/${r.id}/preview?limit=100`, {
        headers: { Accept: "application/json", ...authHeaders },
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d?.detail || `Prévisualisation impossible (${res.status})`);
      setPreview(d as PreviewData);
    } catch (e: any) {
      setPreview(null);
      toast({
        title: "Prévisualisation impossible",
        description: e?.message || "Erreur inconnue",
        variant: "destructive",
      });
    } finally {
      setPreviewBusy(false);
    }
  };

  const doDownload = async (r: ReportOut) => {
    setDownloading((m) => ({ ...m, [r.id]: true }));
    try {
      // Afficher un toast pour confirmer que le téléchargement démarre
      toast({
        title: "Téléchargement en cours...",
        description: `Génération du fichier ${fileFormat.toUpperCase()} pour ${r.name}`,
        duration: 3000,
      });
      
      // Générer un session_id unique pour les logs de progression
      const generateUUID = () => {
        if (typeof crypto !== 'undefined' && crypto.randomUUID) {
          return crypto.randomUUID();
        }
        // Fallback pour les navigateurs qui ne supportent pas crypto.randomUUID
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
          const r = Math.random() * 16 | 0;
          const v = c === 'x' ? r : (r & 0x3 | 0x8);
          return v.toString(16);
        });
      };
      const sessionId = generateUUID();
      
      // Ouvrir le dialog AVANT de lancer le téléchargement pour permettre à la connexion SSE de s'établir
      setDownloadProgress({ reportId: r.id, sessionId });
      
      // Attendre un peu pour que le composant React se monte et la connexion SSE se fasse (1 seconde)
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      const acceptHeader = fileFormat === "csv" ? "text/csv" : fileFormat === "txt" ? "text/plain" : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
      const res = await fetch(`${API_BASE}/reports/${r.id}/download?format=${fileFormat}&session_id=${sessionId}`, {
        method: "GET",
        headers: { Accept: acceptHeader, ...authHeaders },
      });
      if (!res.ok) {
        const msg = await res.text().catch(() => "");
        throw new Error(msg || `Téléchargement impossible (${res.status})`);
      }
      const blob = await res.blob();

      // nom de fichier: {NOM}_{YYYYMMDDHHMMSS}
      const now = new Date();
      const ts = now.getFullYear().toString() + 
                 String(now.getMonth() + 1).padStart(2, '0') + 
                 String(now.getDate()).padStart(2, '0') + 
                 String(now.getHours()).padStart(2, '0') + 
                 String(now.getMinutes()).padStart(2, '0') + 
                 String(now.getSeconds()).padStart(2, '0');
      const safeName = (r.name || `report-${r.id}`)
        .replace(/[^\w\s.-]/g, "")
        .replace(/\s+/g, "_");
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${safeName}_${ts}.${fileFormat}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1500);

      toast({ 
        title: "Téléchargement terminé", 
        description: `Le fichier ${fileFormat.toUpperCase()} a été téléchargé avec succès.`,
      });
    } catch (e: any) {
      toast({
        title: "Téléchargement échoué",
        description: e?.message || "Erreur inconnue",
        variant: "destructive",
      });
    } finally {
      setDownloading((m) => ({ ...m, [r.id]: false }));
      // Garder le dialog ouvert pendant 2 secondes pour voir le message de succès
      setTimeout(() => {
        setDownloadProgress(null);
      }, 2000);
    }
  };

  // ---------- Rendu ----------
  return (
    <div className="min-h-screen bg-background">
      {/* Bandeau */}
      <div className="bg-gradient-primary text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-primary/20 via-transparent to-primary/20" />
        <div className="relative max-w-7xl mx-auto px-6 py-10">
          <div className="text-center animate-in fade-in-0 slide-in-from-top-4 duration-500">
            <div className="inline-flex items-center justify-center gap-3">
              <div className="p-3 rounded-xl bg-white/10 ring-1 ring-white/20 backdrop-blur-sm">
                <Building2 className="h-6 w-6" />
              </div>
              <h1 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-white to-white/80 bg-clip-text text-transparent">
                Plateforme de Reporting
              </h1>
            </div>
            <p className="mt-3 text-white/90 text-lg max-w-3xl mx-auto">
              Automatisation intelligente de vos rapports quotidiens
            </p>
          </div>
        </div>
      </div>

      {/* Contenu */}
      <div className="max-w-7xl mx-auto px-6 py-10">
        <div className="mb-6 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div>
            <h2 className="text-2xl md:text-3xl font-bold text-foreground">Vos Rapports</h2>
            <p className="text-muted-foreground">Cliquez sur un rapport pour afficher les actions.</p>
          </div>
          <div className="flex items-center gap-2 w-full md:w-auto">
            <div className="flex items-center gap-2 w-full md:w-[280px]">
              <Search className="h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Rechercher par nom, ID, description…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
              />
            </div>
            <Button variant="outline" size="sm" className="gap-2" onClick={load}>
              <RefreshCw className="h-4 w-4" />
              Rafraîchir
            </Button>
          </div>
        </div>

        {/* Résumé + pagination (top) */}
        {!loading && (
          <div className="mb-4 flex items-center justify-between text-sm text-muted-foreground">
            <div>
              {total} rapport{total > 1 ? "s" : ""} • page {currentPage} / {totalPages}
              {q.trim() && <span className="ml-2">• filtre : “{q.trim()}”</span>}
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={currentPage <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="gap-1"
              >
                <ChevronLeft className="h-4 w-4" />
                Précédent
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={currentPage >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                className="gap-1"
              >
                Suivant
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}

        {/* Liste */}
        <div className="space-y-4">
          {loading ? (
            <>
              {[...Array(3)].map((_, i) => (
                <Card key={i} className="p-0 overflow-hidden border-glass">
                  <div className="px-4 md:px-6 py-4 flex items-start gap-3">
                    <Skeleton className="h-9 w-9 rounded-lg" />
                    <div className="flex-1 space-y-2">
                      <Skeleton className="h-5 w-3/4" />
                      <Skeleton className="h-4 w-1/2" />
                    </div>
                  </div>
                </Card>
              ))}
            </>
          ) : pageItems.length > 0 ? (
            pageItems.map((r) => {
              const expanded = expandedId === r.id;
              const isDownloading = !!downloading[r.id];
              const isSending = sendingId === r.id;
              return (
                <Card key={r.id} className="p-0 overflow-hidden border-glass hover:shadow-md transition-all duration-200 hover:border-primary/20">
                  <button
                    className="w-full px-4 md:px-6 py-4 flex items-start gap-3 hover:bg-muted/30 transition-all duration-200 active:scale-[0.98]"
                    onClick={() => setExpandedId(expanded ? null : r.id)}
                    aria-expanded={expanded}
                  >
                    <div className="p-2 rounded-lg bg-primary/10 mt-0.5 group-hover:bg-primary/20 transition-colors duration-200">
                      <BarChart3 className="h-5 w-5 text-primary" />
                    </div>
                    <div className="flex-1 text-left min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <h3 className="text-base md:text-lg font-semibold truncate">{r.name}</h3>
                        {expanded ? (
                          <ChevronUp className="h-5 w-5 text-muted-foreground" />
                        ) : (
                          <ChevronDown className="h-5 w-5 text-muted-foreground" />
                        )}
                      </div>
                      <p className="mt-1 text-sm text-muted-foreground line-clamp-1">
                        {r.description ||
                          (r.sql ? "Requête SQL" : r.procedure ? `Procédure: ${r.procedure}` : "")}
                      </p>
                    </div>
                  </button>

                  {expanded && (
                    <div className="px-4 md:px-6 pb-4 pt-2 border-t bg-background/50 animate-in slide-in-from-top-2 duration-200">
                      <div className="flex flex-wrap items-center gap-2">
                        <Button
                          variant="outline"
                          className="gap-2 hover:bg-primary/5 hover:border-primary/30 transition-all duration-200"
                          onClick={() => doPreview(r)}
                          title="Exécuter et afficher les 100 premières lignes"
                          disabled={previewBusy}
                        >
                          <Eye className="h-4 w-4" />
                          {previewBusy ? (
                            <span className="flex items-center gap-2">
                              <span className="w-4 h-4 border-2 border-current/40 border-t-current rounded-full animate-spin" />
                              Prévisualisation…
                            </span>
                          ) : (
                            "Prévisualiser"
                          )}
                        </Button>

                        {/* Sélecteur de format */}
                        <Select value={fileFormat} onValueChange={(v) => setFileFormat(v as "csv" | "txt" | "xlsx")}>
                          <SelectTrigger className="w-[120px]">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="csv">CSV</SelectItem>
                            <SelectItem value="txt">TXT</SelectItem>
                            <SelectItem value="xlsx">Excel</SelectItem>
                          </SelectContent>
                        </Select>

                        <Button
                          variant="outline"
                          className="gap-2 hover:bg-primary/5 hover:border-primary/30 transition-all duration-200"
                          onClick={() => doDownload(r)}
                          title={`Télécharger ${fileFormat.toUpperCase()}`}
                          disabled={isDownloading}
                        >
                          <Download className="h-4 w-4" />
                          {isDownloading ? (
                            <span className="flex items-center gap-2">
                              <span className="w-4 h-4 border-2 border-current/40 border-t-current rounded-full animate-spin" />
                              Téléchargement…
                            </span>
                          ) : (
                            `Télécharger ${fileFormat.toUpperCase()}`
                          )}
                        </Button>
                      </div>
                    </div>
                  )}
                </Card>
              );
            })
          ) : (
            <Card className="p-8 bg-muted/30">
              <p className="text-muted-foreground">
                {q.trim()
                  ? "Aucun rapport ne correspond à votre recherche."
                  : "Aucun rapport disponible pour votre compte."}
              </p>
            </Card>
          )}
        </div>

        {/* Pagination (bottom) */}
        {!loading && total > 0 && totalPages > 1 && (
          <div className="mt-6 flex items-center justify-between text-sm">
            <div className="text-muted-foreground">
              {total} rapport{total > 1 ? "s" : ""} • page {currentPage} / {totalPages}
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={currentPage <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="gap-1"
              >
                <ChevronLeft className="h-4 w-4" />
                Précédent
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={currentPage >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                className="gap-1"
              >
                Suivant
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}

        {/* Info bloc */}
        <Card className="mt-8 p-6 bg-gradient-card backdrop-blur-sm border-glass shadow-card text-center hover:shadow-md transition-all duration-200">
          <h3 className="text-lg font-semibold text-foreground mb-4">Besoin d'aide avec vos rapports ?</h3>
          <p className="text-muted-foreground mb-6">
            Contactez le support technique pour toute question concernant la génération ou l'envoi de rapports.
          </p>
          <div className="flex items-center justify-center gap-2 text-primary hover:text-primary/80 transition-colors duration-200">
            <Mail className="h-4 w-4" />
            <span className="text-sm font-medium">disreport@dislogroup.com</span>
          </div>
        </Card>
      </div>

      {/* Dialog Preview */}
      <Dialog
        open={previewOpen}
        onOpenChange={(o) => {
          setPreviewOpen(o);
          if (!o) {
            setPreview(null);
            setPreviewBusy(false);
            setPreviewTitle("");
          }
        }}
      >
        <DialogContent className="max-w-[95vw] md:max-w-5xl" aria-describedby="preview-desc">
          <DialogHeader>
            <DialogTitle>{previewTitle || "Prévisualisation"}</DialogTitle>
            <DialogDescription id="preview-desc">
              Affiche jusqu’à 100 lignes pour un aperçu rapide (les types sont convertis en texte).
            </DialogDescription>
          </DialogHeader>

          {previewBusy ? (
            <div className="p-6 space-y-4">
              <div className="space-y-2">
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-5/6" />
                <Skeleton className="h-4 w-4/6" />
              </div>
              <div className="space-y-2 pt-4">
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-5/6" />
                <Skeleton className="h-4 w-4/6" />
              </div>
            </div>
          ) : preview && preview.columns?.length ? (
            <div className="mt-2 border rounded-md overflow-auto max-h-[70vh]">
              <table className="w-full text-sm">
                <thead className="bg-muted sticky top-0">
                  <tr>
                    {preview.columns.map((c) => (
                      <th key={c} className="px-3 py-2 text-left font-medium border-b">
                        {c}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {preview.rows.map((row, idx) => (
                    <tr key={idx} className="even:bg-muted/20">
                      {preview.columns.map((c) => (
                        <td key={c} className="px-3 py-2 border-b align-top">
                          {String(row?.[c] ?? "")}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="p-6 text-sm text-muted-foreground">Aucune donnée à afficher.</div>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog de progression pour les téléchargements */}
      <Dialog open={downloadProgress !== null} onOpenChange={(open) => {
        if (!open) setDownloadProgress(null);
      }}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Progression du téléchargement</DialogTitle>
            <DialogDescription>
              Suivi en temps réel de la génération et du téléchargement du fichier
            </DialogDescription>
          </DialogHeader>
          {downloadProgress ? (
            <ProgressLogger
              sessionId={downloadProgress.sessionId}
              reportId={downloadProgress.reportId}
              onComplete={() => {
                toast({
                  title: "✅ Téléchargement terminé",
                  description: "Le fichier a été généré avec succès",
                });
              }}
              onError={(error) => {
                toast({
                  title: "❌ Erreur lors du téléchargement",
                  description: error,
                  variant: "destructive",
                });
              }}
            />
          ) : (
            <div className="p-4 text-center text-muted-foreground">
              <p>En attente...</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
